import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { listVoices } from "@/lib/myVoicesApi";

const sel = "w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white focus:border-cyan-300/50 focus:outline-none";
const lbl = "mb-1 mt-4 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50";
const PITCHES = Array.from({ length: 25 }, (_, i) => i - 12);

/** Which trained voice sings, and an optional pitch shift. `value = {voice_id, pitch}`; `onReady(bool)` when a voice is usable. */
export default function VoicePicker({ value, onChange, onReady }) {
  const [voices, setVoices] = useState(null);
  useEffect(() => {
    listVoices().then((v) => {
      const ready = v.filter((x) => x.active_version?.status === "ready");
      setVoices(ready);
      const pick = ready.find((x) => x.selected) || ready[0];
      if (pick) onChange({ ...value, voice_id: pick.id });
      onReady?.(Boolean(pick));
    }).catch(() => { setVoices([]); onReady?.(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (voices === null) return <Loader2 className="mt-4 h-5 w-5 animate-spin text-white/40" />;
  if (!voices.length) {
    return (
      <p data-testid="voice-picker-none" className="mt-4 rounded-2xl border border-amber-300/25 bg-amber-300/10 p-3 text-[13px] text-amber-50">
        You don't have a trained voice yet. <Link to="/create/my-voices" className="font-semibold underline underline-offset-4">Train it in My Voices</Link> first — then come back here.
      </p>
    );
  }
  return (
    <div>
      <label className={lbl} htmlFor="vp-voice">Your voice</label>
      <select id="vp-voice" data-testid="voice-picker-voice" className={sel} value={value.voice_id || ""} onChange={(e) => onChange({ ...value, voice_id: e.target.value })}>
        {voices.map((v) => <option key={v.id} value={v.id}>{v.name} · version {v.active_version.n}{v.selected ? " (selected)" : ""}</option>)}
      </select>
      <label className={lbl} htmlFor="vp-pitch">Pitch shift</label>
      <select id="vp-pitch" data-testid="voice-picker-pitch" className={sel} value={value.pitch} onChange={(e) => onChange({ ...value, pitch: Number(e.target.value) })}>
        {PITCHES.map((n) => <option key={n} value={n}>{n === 0 ? "None — the song is in a male range" : `${n > 0 ? "+" : ""}${n} semitones`}</option>)}
      </select>
      <p className="mt-1.5 text-[11px] text-white/45">Use −12 if a female singer leads the song an octave above you.</p>
    </div>
  );
}
